import React from "react"
import { Icon } from "../../shared"
import { Segmented } from "../../shared/components"
import { HURUF, PILIHAN_HALANGAN, labelAktif, labelHalangan, jumlahAktif } from "../../lib/reportFilters"

/* ============================================================
   Bilah saringan untuk tab laporan yang berisi daftar peserta.

   Hanya mengubah pilihan; menyaring adalah tugas server (lihat
   lib/reportFilters.js), jadi yang tampil di tabel sama dengan yang
   masuk ke berkas unduhan.

   Props:
     saringan        nilai saat ini (bentuk SARINGAN_KOSONG)
     ubah(patch)     menggabungkan perubahan
     hapusSemua()    mengosongkan saringan, urutan dibiarkan
     hurufHitung     Map huruf → jumlah nama (dari server), atau null
     perusahaan      daftar nama perusahaan di cakupan ini
     hitung          { semua, layak, belum } untuk segmen sertifikat
     jumlah          { tampil, total } — "Menampilkan x dari y"
     lainnya         saringan tambahan yang ditawarkan: halangan, tes, hadir, identitas
   ============================================================ */

const URUTAN = [
  ["nama", "asc", "Nama (A–Z)", "Name (A–Z)"],
  ["nama", "desc", "Nama (Z–A)", "Name (Z–A)"],
  ["perusahaan", "asc", "Perusahaan (A–Z)", "Company (A–Z)"],
  ["perusahaan", "desc", "Perusahaan (Z–A)", "Company (Z–A)"],
  ["posttest", "desc", "Posttest tertinggi", "Highest posttest"],
  ["posttest", "asc", "Posttest terendah", "Lowest posttest"],
  ["pretest", "desc", "Pretest tertinggi", "Highest pretest"],
  ["pretest", "asc", "Pretest terendah", "Lowest pretest"],
  ["gain", "desc", "Gain tertinggi", "Highest gain"],
  ["gain", "asc", "Gain terendah", "Lowest gain"],
  ["kehadiran", "asc", "Kehadiran terendah", "Lowest attendance"],
  ["kehadiran", "desc", "Kehadiran tertinggi", "Highest attendance"],
  ["sertifikat", "desc", "Layak sertifikat dulu", "Eligible first"],
  ["sertifikat", "asc", "Belum layak dulu", "Not yet eligible first"],
]

// Pencarian dikirim setelah admin berhenti mengetik sebentar: tiap huruf yang
// diketik tidak perlu menjadi satu permintaan ke server.
const JEDA_KETIK_MS = 300

export default function ReportFilterBar({
  L, saringan, ubah, hapusSemua, hurufHitung, perusahaan = [], hitung, jumlah, lainnya = [],
}) {
  const [teks, setTeks] = React.useState(saringan.q || "")
  // Disetel ulang dari luar (Hapus semua, ganti kelas): kotak ikut kosong.
  React.useEffect(() => { setTeks(saringan.q || "") }, [saringan.q])
  React.useEffect(() => {
    if (teks === (saringan.q || "")) return undefined
    const t = setTimeout(() => ubah({ q: teks }), JEDA_KETIK_MS)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teks])

  const aktif = labelAktif(saringan, L)
  const jumlahLainnya = lainnya.filter((k) => saringan[k]).length
  const nilaiUrut = `${saringan.urut}:${saringan.arah}`

  return (
    <div className="report-filterbar">
      <div className="report-toolbar">
        <div className="search-wrap">
          <Icon name="search" size={14} />
          <input
            className="search-input"
            type="search"
            value={teks}
            onChange={(e) => setTeks(e.target.value)}
            placeholder={L("Cari nama, email, atau perusahaan", "Search name, email, or company")}
            aria-label={L("Cari peserta", "Search participants")}
            maxLength={100}
          />
        </div>
        {hitung && (
          <Segmented
            value={saringan.status}
            onChange={(v) => ubah({ status: v })}
            options={[
              { value: "", label: `${L("Semua", "All")} · ${hitung.semua}` },
              { value: "layak", label: `${L("Layak", "Eligible")} · ${hitung.layak}` },
              { value: "belum", label: `${L("Belum layak", "Not yet")} · ${hitung.belum}` },
            ]}
          />
        )}
        {perusahaan.length > 0 && (
          <select
            className="input report-filter-select"
            value={saringan.perusahaan}
            onChange={(e) => ubah({ perusahaan: e.target.value })}
            aria-label={L("Perusahaan", "Company")}
          >
            <option value="">{L("Semua perusahaan", "All companies")}</option>
            {perusahaan.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <select
          className="input report-filter-select"
          value={nilaiUrut}
          onChange={(e) => { const [urut, arah] = e.target.value.split(":"); ubah({ urut, arah }) }}
          aria-label={L("Urutkan", "Sort by")}
        >
          {URUTAN.map(([u, a, id, en]) => (
            <option key={`${u}:${a}`} value={`${u}:${a}`}>{L("Urut: ", "Sort: ")}{L(id, en)}</option>
          ))}
        </select>
        <span className="report-toolbar-count" aria-live="polite">
          {L(`Menampilkan ${jumlah.tampil} dari ${jumlah.total}`, `Showing ${jumlah.tampil} of ${jumlah.total}`)}
        </span>
      </div>

      {/* Deretan huruf. Hitungannya dari server (bidang `huruf`); tanpa itu
          deretan ini tidak ditampilkan daripada menebak definisinya. */}
      {hurufHitung && (
        <div className="report-letters" role="group" aria-label={L("Huruf awal nama", "Name initial")}>
          <button
            type="button"
            className={`report-letter wide${saringan.huruf ? "" : " active"}`}
            aria-pressed={!saringan.huruf}
            onClick={() => ubah({ huruf: "" })}
          >
            {L("Semua", "All")}
          </button>
          {HURUF.map((h) => {
            const n = hurufHitung.get(h) || 0
            const dipilih = saringan.huruf === h
            return (
              <button
                key={h}
                type="button"
                className={`report-letter${dipilih ? " active" : ""}`}
                aria-pressed={dipilih}
                disabled={n === 0 && !dipilih}
                title={h === "#"
                  ? L(`Awal nama bukan huruf: ${n}`, `Non-letter start: ${n}`)
                  : L(`${h}: ${n} peserta`, `${h}: ${n} participants`)}
                onClick={() => ubah({ huruf: dipilih ? "" : h })}
              >
                {h}
              </button>
            )
          })}
        </div>
      )}

      {lainnya.length > 0 && (
        <details className="report-more" open={jumlahLainnya > 0 || undefined}>
          <summary>
            {L("Saringan lain", "More filters")}
            {jumlahLainnya > 0 && <span className="report-more-count">{jumlahLainnya}</span>}
          </summary>
          <div className="report-more-body">
            {lainnya.includes("halangan") && (
              <label className="report-more-field">
                <span>{L("Alasan belum layak", "Not-yet-eligible reason")}</span>
                <select className="input" value={saringan.halangan} onChange={(e) => ubah({ halangan: e.target.value })}>
                  <option value="">{L("Semua", "Any")}</option>
                  {PILIHAN_HALANGAN.map((k) => <option key={k} value={k}>{labelHalangan(k, L)}</option>)}
                </select>
              </label>
            )}
            {lainnya.includes("tes") && (
              <label className="report-more-field">
                <span>{L("Tes", "Tests")}</span>
                <select className="input" value={saringan.tes} onChange={(e) => ubah({ tes: e.target.value })}>
                  <option value="">{L("Semua", "Any")}</option>
                  <option value="belum_pretest">{L("Belum mengerjakan pretest", "Pretest not taken")}</option>
                  <option value="belum_posttest">{L("Belum mengerjakan posttest", "Posttest not taken")}</option>
                </select>
              </label>
            )}
            {lainnya.includes("hadir") && (
              <label className="report-more-check">
                <input type="checkbox" checked={saringan.hadir === "rendah"} onChange={(e) => ubah({ hadir: e.target.checked ? "rendah" : "" })} />
                <span>{L("Kehadiran di bawah ambang", "Attendance below threshold")}</span>
              </label>
            )}
            {lainnya.includes("identitas") && (
              <label className="report-more-check">
                <input type="checkbox" checked={saringan.identitas === "belum_lengkap"} onChange={(e) => ubah({ identitas: e.target.checked ? "belum_lengkap" : "" })} />
                <span>{L("Data identitas belum lengkap", "Identity data incomplete")}</span>
              </label>
            )}
          </div>
        </details>
      )}

      {jumlahAktif(saringan) > 0 && (
        <div className="report-chips">
          {aktif.map((c) => (
            <button
              key={c.kunci}
              type="button"
              className="report-chip"
              onClick={() => ubah({ [c.kunci]: "" })}
              aria-label={L(`Lepas saringan ${c.label}`, `Remove filter ${c.label}`)}
            >
              {c.label}
              <Icon name="close" size={11} />
            </button>
          ))}
          <button type="button" className="report-chip-clear" onClick={hapusSemua}>
            {L("Hapus semua", "Clear all")}
          </button>
        </div>
      )}
    </div>
  )
}
